import { useState } from "react";
import { MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { generateContactWhatsAppLink } from "@/utils/whatsapp";
import { toast } from "sonner";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const whatsappMessage = `Hola, soy ${formData.name}.
Email: ${formData.email}
Mensaje: ${formData.message}`;

    const link = generateContactWhatsAppLink(whatsappMessage);
    window.open(link, "_blank");
    toast.success("Redirigiendo a WhatsApp...");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleWhatsAppDirect = () => {
    const link = generateContactWhatsAppLink("Hola, me gustaría hacer una consulta.");
    window.open(link, "_blank");
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Contacto"
        description="Contactanos para consultas sobre productos para mascotas. WhatsApp, teléfono y formulario de contacto disponibles. Atención personalizada en San José, Costa Rica."
        keywords="contacto, tienda mascotas, WhatsApp, Costa Rica, San José"
      />
      <Header />

      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-pastel-green/20 via-background to-pastel-blue/20 py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Contactanos</h1>
              <p className="text-xl text-muted-foreground">¿Tenés alguna consulta? Estamos aquí para ayudarte</p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              {/* Contact Form */}
              <div className="bg-card rounded-3xl p-8 shadow-card">
                <h2 className="text-2xl font-bold mb-6">Envianos un Mensaje</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Nombre *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Tu nombre"
                      className="h-12 rounded-2xl"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@email.com"
                      className="h-12 rounded-2xl"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Mensaje *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Contanos en qué podemos ayudarte..."
                      rows={6}
                      className="rounded-2xl resize-none"
                    />
                  </div>

                  <Button type="submit" size="lg" variant="pill" className="w-full">
                    <MessageCircle className="mr-2" />
                    Enviar por WhatsApp
                  </Button>
                </form>
              </div>

              {/* Contact Info */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold mb-6">Información de Contacto</h2>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-pastel-green/20 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">Dirección</h3>
                        <p className="text-muted-foreground">San José, Costa Rica</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-pastel-pink/20 flex items-center justify-center flex-shrink-0">
                        <Phone className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">Teléfono</h3>
                        <p className="text-muted-foreground">+506 6449 8772</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                        <Mail className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">Email</h3>
                        <p className="text-muted-foreground">docapetshopcr@gmail.com</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* WhatsApp CTA */}
                <div className="bg-gradient-to-br from-pastel-green/20 to-pastel-blue/20 rounded-3xl p-8">
                  <h3 className="text-xl font-bold mb-3">¿Preferís WhatsApp?</h3>
                  <p className="text-muted-foreground mb-6">
                    Hablá directamente con nosotros para una respuesta más rápida
                  </p>
                  <Button onClick={handleWhatsAppDirect} size="lg" variant="pill" className="w-full">
                    <MessageCircle className="mr-2" />
                    Abrir WhatsApp
                  </Button>
                </div>

                {/* Business Hours */}
                <div className="bg-muted rounded-3xl p-8">
                  <h3 className="text-xl font-bold mb-4">Horarios de Atención</h3>
                  <div className="space-y-2 text-muted-foreground">
                    <p>Lunes a Viernes: 9:00 - 19:00</p>
                    <p>Sábados: 10:00 - 14:00</p>
                    <p>Domingos: Cerrado</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
